/**
 * Fake Player Generator
 * Generates realistic gamer usernames and obscure ELO ratings for seamless bot matchmaking
 */

// Realistic username components that gamers actually use
const prefixes = ["xX", "Pro_", "Dark", "Elite", "The", "King", "Queen", "Shadow", "Epic", ""];
const words = [
  "Knight", "Rook", "Bishop", "Pawn", "Castle", "Gambit",
  "Shadow", "Storm", "Dragon", "Phoenix", "Ninja", "Savage",
  "Legend", "Boss", "Wolf", "Tiger", "Hawk", "Viper", "Cobra",
  "Ace", "Flash", "Blaze", "Frost", "Thunder", "Chaos", "Nova",
  "Sniper", "Hunter", "Warrior", "Titan", "Ghost", "Reaper",
  "Chess", "Check", "Mate", "Grand", "Master", "Tactical"
];
const suffixes = ["_", "", "99", "23", "12", "007", "X", "HD", "YT", "TV", "Pro", "GG", ""];
const numbers = ["", "42", "88", "69", "100", "2024", "777", "13", "21", "420", "1337", ""];

/**
 * Generate a realistic gamer username
 * Examples: "Knockout_12", "xXDragonSlayerXx", "ChessMaster2024", "ProKnight007"
 */
export function generateFakeUsername(): string {
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const word1 = words[Math.floor(Math.random() * words.length)];
  const word2 = Math.random() > 0.6 ? words[Math.floor(Math.random() * words.length)] : "";
  const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
  const number = Math.random() > 0.5 ? numbers[Math.floor(Math.random() * numbers.length)] : "";
  
  // Various username patterns
  const patterns = [
    `${prefix}${word1}${word2}${suffix}${number}`,
    `${word1}${word2}_${number}`,
    `${prefix}${word1}${suffix}`,
    `${word1}${word2}${number}`,
    `${word1}_${word2}`,
  ];
  
  const username = patterns[Math.floor(Math.random() * patterns.length)]
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');
  
  return username || "ChessPlayer" + Math.floor(Math.random() * 9999);
}

/**
 * Generate a realistic obscure ELO near the player's rating
 * e.g., playerElo=1200 → 1147, 1283, 1159, 1312, etc.
 * 
 * @param playerElo - The player's current ELO rating
 * @returns An obscure ELO rating within ±150 of player's rating
 */
export function generateFakeElo(playerElo: number): number {
  // ±150 range with random noise to make it feel real
  const variance = Math.floor(Math.random() * 300) - 150; // -150 to +150
  const noise = Math.floor(Math.random() * 97) - 48; // Additional randomness for obscure numbers
  const fakeElo = playerElo + variance + noise;
  
  // Keep it within reasonable chess ELO bounds
  return Math.max(400, Math.min(3000, fakeElo));
}

/**
 * Get a random search delay between 3-20 seconds
 * This makes matchmaking feel realistic and varied
 * 
 * @returns Delay in milliseconds (3000-20000)
 */
export function getSearchDelay(): number {
  return Math.floor(Math.random() * 17000) + 3000; // 3000-20000ms
}
